'use client'
import { useState } from 'react'
import Link from 'next/link'
import Navbar from '@/components/Navbar'

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '' })
  const [status, setStatus] = useState<'idle'|'sending'|'sent'|'error'>('idle')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setStatus('sending')
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      })
      setStatus(res.ok ? 'sent' : 'error')
    } catch {
      setStatus('error')
    }
  }

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <Navbar activePage="home" />

      <section className="pt-28 pb-20 px-6">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-16 items-start">

          {/* Left */}
          <div>
            <div className="text-[11px] font-semibold tracking-widest uppercase text-gray-400 mb-4">Get in Touch</div>
            <h1 style={{fontFamily:"'Cormorant Garamond',Georgia,serif",fontWeight:600,fontSize:'clamp(36px,5vw,56px)',lineHeight:1.06}} className="text-[#1a1a1a] mb-6">
              Let&apos;s talk about<br/><em style={{fontStyle:'italic',color:'#aaa'}}>your AI department</em>
            </h1>
            <p className="text-base text-gray-500 leading-relaxed mb-10 max-w-sm">
              Whether you&apos;re ready to hire your first AI employee or want to deploy a full department — we&apos;re here to help.
            </p>

            <div className="flex flex-col gap-5">
              {[
                { label: 'General Enquiries', value: 'info@preplix.ai', href: 'mailto:info@preplix.ai', icon: (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                )},
                { label: 'Support', value: 'support@preplix.ai', href: 'mailto:support@preplix.ai', icon: (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                )},
                { label: 'Privacy & Legal', value: 'legal@preplix.ai', href: 'mailto:legal@preplix.ai', icon: (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                )},
              ].map(c => (
                <a key={c.label} href={c.href} className="flex items-center gap-4 group">
                  <div className="w-10 h-10 bg-white border border-[#E8E8E4] rounded-xl flex items-center justify-center text-gray-400 group-hover:border-[#1a1a1a] group-hover:text-[#1a1a1a] transition-all">
                    {c.icon}
                  </div>
                  <div>
                    <div className="text-xs text-gray-400">{c.label}</div>
                    <div className="text-sm font-medium text-[#1a1a1a] group-hover:underline">{c.value}</div>
                  </div>
                </a>
              ))}
            </div>
          </div>

          {/* Right — Form */}
          <div className="bg-white border border-[#E8E8E4] rounded-2xl p-8">
            {status === 'sent' ? (
              <div className="text-center py-12">
                <div className="w-12 h-12 bg-emerald-50 border border-emerald-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
                <h3 className="font-semibold text-[#1a1a1a] mb-2">Message sent!</h3>
                <p className="text-sm text-gray-500">We&apos;ll get back to you within 24 hours.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <h2 className="text-lg font-semibold text-[#1a1a1a] mb-2">Send us a message</h2>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">Name</label>
                    <input required value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="Your name" className="w-full px-3.5 py-2.5 border border-[#E8E8E4] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1a1a1a] focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">Email</label>
                    <input required type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} placeholder="you@company.com" className="w-full px-3.5 py-2.5 border border-[#E8E8E4] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1a1a1a] focus:border-transparent" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">Company <span className="text-gray-300">(optional)</span></label>
                  <input value={form.company} onChange={e=>setForm({...form,company:e.target.value})} placeholder="Your company name" className="w-full px-3.5 py-2.5 border border-[#E8E8E4] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1a1a1a] focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">Message</label>
                  <textarea required rows={5} value={form.message} onChange={e=>setForm({...form,message:e.target.value})} placeholder="Tell us about your business and what you need..." className="w-full px-3.5 py-2.5 border border-[#E8E8E4] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1a1a1a] focus:border-transparent resize-none" />
                </div>
                {status === 'error' && <p className="text-sm text-red-500">Something went wrong. Please email us directly at info@preplix.ai</p>}
                <button type="submit" disabled={status==='sending'} className="bg-[#1a1a1a] text-white px-6 py-3 rounded-xl text-sm font-semibold hover:bg-black transition disabled:opacity-50 mt-1">
                  {status === 'sending' ? 'Sending…' : 'Send Message →'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <footer className="py-8 px-6 border-t border-[#E8E8E4] bg-[#FAFAF8]">
        <div className="max-w-5xl mx-auto flex flex-wrap justify-between items-center gap-4 text-xs text-gray-400">
          <span>© 2026 Preplix. All rights reserved.</span>
          <div className="flex gap-5">
            <Link href="/about" className="hover:text-[#1a1a1a]">About</Link>
            <Link href="/terms" className="hover:text-[#1a1a1a]">Terms</Link>
            <Link href="/privacy" className="hover:text-[#1a1a1a]">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  )
}
